import { useEffect, useRef } from 'react';
import { useConsumer } from './CableContext';

/**
 * Subscribes to an Action Cable channel for the lifetime of the calling
 * component. Replaces the `<ActionCable channel onReceived ... />` element from
 * the old `react-actioncable-provider`.
 *
 * Handlers are read through a ref so that a handler changing identity between
 * renders (e.g. a `useCallback` whose deps changed) does NOT tear down and
 * recreate the underlying WebSocket subscription. The subscription is only
 * recreated when the consumer or the channel identity changes.
 *
 * @param {Object} params
 * @param {Object|string} params.channel - channel descriptor passed to
 *   `subscriptions.create`, e.g. `{ channel: 'CodesChannel', project: id }`.
 * @param {Function} [params.onReceived]
 * @param {Function} [params.onConnected]
 * @param {Function} [params.onDisconnected]
 * @param {Function} [params.onRejected]
 */
export function useCableSubscription({
  channel,
  onReceived,
  onConnected,
  onDisconnected,
  onRejected
}) {
  const consumer = useConsumer();

  const handlers = useRef({});
  handlers.current = { onReceived, onConnected, onDisconnected, onRejected };

  // The channel descriptor is typically an inline object (new identity every
  // render), so key the effect on its stringified value rather than the object.
  const channelKey = JSON.stringify(channel);

  useEffect(() => {
    if (!consumer) return undefined;

    const subscription = consumer.subscriptions.create(channel, {
      received: (data) => handlers.current.onReceived && handlers.current.onReceived(data),
      connected: () => handlers.current.onConnected && handlers.current.onConnected(),
      disconnected: () => handlers.current.onDisconnected && handlers.current.onDisconnected(),
      rejected: () => handlers.current.onRejected && handlers.current.onRejected()
    });

    return () => subscription.unsubscribe();
    // `channel` is intentionally omitted; `channelKey` captures its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumer, channelKey]);
}

export default useCableSubscription;
